import styles from '../ProductPageLayout/ProductPageLayout.module.css'
import type { PricingQuoteResult } from '~/utils/pricingQuote'
import React, {
  FC,
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from 'react'
import { useRouter } from '~/hooks/useRouter'
import { ConfiguratorPageShell } from '~/components/ConfiguratorPageShell/ConfiguratorPageShell'
import { FormattedMessage } from 'react-intl'
import { useMediaQuery } from '@mui/material'
import { cdn } from '~/lib/cdn'

import {
  ProductDimensions,
  ProductDimensionsComponent,
} from '~/components/ProductPage/productTypeComponents/ProductDimensions'
import {
  ProductColors,
  ProductColorsComponent,
} from '~/components/ProductPage/productTypeComponents/ProductColors'
import {
  ProductSelect,
  ProductSelectComponent,
} from '~/components/ProductPage/productTypeComponents/ProductSelect'
import {
  ProductColumns,
  ProductColumnsComponent,
} from '~/components/ProductPage/productTypeComponents/ProductColumns'
import {
  ProductIndividualColumns,
  ProductIndividualColumnsComponent,
} from '~/components/ProductPage/productTypeComponents/stand/ProductIndividualColumns'
import type { ButtonOptionsType } from '~/components/ButtonSelect/ButtonSelect'
import { ButtonSelect } from '~/components/ButtonSelect/ButtonSelect'

import {
  ProductFurniture,
  ProductFurnitureComponent,
  ProductFurniturePredefinedValue,
} from '~/components/ProductPage/productTypeComponents/ProductFurniture'
import {
  ProductPrice,
  ProductPriceComponent,
} from '~/components/ProductPage/productTypeComponents/ProductPrice'
import { ProductMetadataComponent } from '~/components/ProductPage/productTypeComponents/ProductMetadata'
import { ProductGalleryComponent } from '~/components/ProductPage/productTypeComponents/ProductGallery'
import { ProductGalleryColorsConfig } from '~/components/ProductPage/productTypeComponents/ProductGalleryColors'

import {
  FurnitureViewer,
  FurnitureViewerRef,
} from '~/components/ThreeDModel/FurnitureViewer'
import { use3DFurnitureProps } from '~/hooks/use3DFurnitureProps'
import { useCart } from '~/context/cartContext'
import { Dimension } from '~/components/ProductListPage/products'
import { DEFAULT_STAND } from './productTypes/stand'
import { useConfiguratorConfigOptional } from '~/context/urlConfigContext'
import { ShareConfigModal } from '~/components/ShareConfigModal/ShareConfigModal'
import { getColorItemByName } from '~/utils/colorDictionary'
import { useFurniture } from '~/context/furnitureContext'
import { findChipboardIdByColorName } from '~/utils/furnitureBySku'
import { StandQuoteProvider } from '~/context/standQuoteContext'
import { ColumnConfigurationType } from '~/types/columnConfigurationTypes'

// Mobile section labels & order
import {
  NavSection,
  NAV_ORDER,
  SECTION_LABELS,
  isNavSection,
} from '~/components/ProductPage/productTypeComponents/sectionRegistry'

export type ProductComponent =
  | ProductGalleryComponent
  | ProductDimensionsComponent
  | ProductColorsComponent
  | ProductSelectComponent
  | ProductColumnsComponent
  | ProductIndividualColumnsComponent
  | ProductFurnitureComponent
  | ProductPriceComponent
  | ProductMetadataComponent
  | ProductGalleryColorsConfig

export type PredefinedValue = {
  sections?: number
  columns?: number
  gallery?: string[]
  imageSelect?: string
  imageCarousel?: string[]
  dimensions?: Dimension
  colors?: string
  galleryColors?: string
  select?: string
  furniture?: ProductFurniturePredefinedValue
  price?: number
  screenshot?: string
  quote?: PricingQuoteResult
}

interface ProductPageProps {
  components: () => ProductComponent[]
  name: string
  values?: PredefinedValue
}

// Helpers
const filterNavigable = (components: ProductComponent[]) =>
  components.filter((c): c is Extract<ProductComponent, { type: NavSection }> =>
    isNavSection(c.type as string)
  )

export const ProductPage: FC<ProductPageProps> = ({
  components,
  name,
  values,
}) => {
  const router = useRouter()
  const locale = (router.query.locale as string) || 'ro'
  const { addItem } = useCart()
  const isMobile = useMediaQuery('(max-width: 768px)')
  const [activeColumnTab, setActiveColumnTab] = useState(0)
  const [selectedColumnIndex, setSelectedColumnIndex] = useState<number | null>(
    null
  )
  const deselectColumnRef = useRef<(() => void) | null>(null)
  const furnitureViewerRef = useRef<FurnitureViewerRef>(null)
  const [isShareModalOpen, setIsShareModalOpen] = useState(false)

  // Get current config for share functionality
  const urlConfigCtx = useConfiguratorConfigOptional()

  const currentComponents = components()
  const furniture3DProps = use3DFurnitureProps(
    currentComponents,
    values,
    DEFAULT_STAND,
    false, // isWardrobe = false
    'stand' // furnitureType
  )

  // Resolve the selected color to a backend chipboardId for the pricing quote.
  const furniture = useFurniture()
  const colorsComp = currentComponents.find((c) => c.type === 'colors') as
    | ProductColorsComponent
    | undefined
  const chipboardId = useMemo(
    () => findChipboardIdByColorName(furniture, colorsComp?.selectedColor),
    [furniture, colorsComp?.selectedColor]
  )

  const renderFileURL = useMemo(() => {
    const colorItem = colorsComp?.selectedColor
      ? getColorItemByName(colorsComp.selectedColor)
      : undefined
    return colorItem?.fileURL
  }, [colorsComp])

  const renderImages = useMemo(() => {
    if (!renderFileURL) return undefined
    const base = cdn('/cdn/stand/renders/large')
    return [1, 2].map((i) => `${base}/${i}/${renderFileURL}.jpg`)
  }, [renderFileURL])

  const renderThumbnail = useMemo(() => {
    if (!renderFileURL) return undefined
    return cdn(`/cdn/stand/renders/thumbnails/1/${renderFileURL}.jpg`)
  }, [renderFileURL])

  const priceComponent = currentComponents.find((c) => c.type === 'price') as
    | ProductPriceComponent
    | undefined

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const handleColumnClick = useCallback((index: number | null) => {
    if (index !== null) {
      // Column clicked: update both activeTab and selectedColumn
      setActiveColumnTab(index)
      setSelectedColumnIndex(index)
    } else {
      // Background clicked: only deselect column, keep activeTab
      setSelectedColumnIndex(null)
    }
  }, [])
  const handleDeselectFunctionReady = useCallback((fn: () => void) => {
    deselectColumnRef.current = fn
  }, [])

  // ---------- DESKTOP renderer (unchanged stack) ----------
  const getComponent = (component: ProductComponent): React.ReactNode => {
    switch (component.type) {
      case 'dimensions':
        return (
          <ProductDimensions
            configuration={component}
            predefinedValue={values?.dimensions ?? undefined}
          />
        )
      case 'colors':
        return (
          <ProductColors
            configuration={component}
            predefinedValue={values?.colors ?? undefined}
          />
        )
      case 'columns':
        return (
          <ProductColumns
            configuration={component}
            predefinedValue={values?.columns ?? undefined}
            options={(component as ProductColumnsComponent).options}
          />
        )
      case 'individualColumns':
        return (
          <ProductIndividualColumns
            configuration={component}
            activeTab={activeColumnTab}
            onActiveTabChange={setActiveColumnTab}
            onActiveColumnChange={setSelectedColumnIndex}
          />
        )
      case 'select':
        return (
          <ProductSelect
            configuration={component}
            predefinedValue={values?.select ?? undefined}
          />
        )
      case 'furniture': {
        const furnitureConfig = {
          ...(component as ProductFurnitureComponent),
          is3DEnabled: true,
        }
        return (
          <ProductFurniture
            configuration={{ ...furnitureConfig, furnitureType: 'stand' }}
            predefinedValue={values?.furniture ?? undefined}
          />
        )
      }
      default:
        return null
    }
  }

  // ---------- MOBILE: chips + core-only panel ----------
  // Build available sections (present + valid for current mode)
  const navComponents = filterNavigable(currentComponents).sort(
    (a, b) =>
      NAV_ORDER.indexOf(a.type as NavSection) -
      NAV_ORDER.indexOf(b.type as NavSection)
  )

  const [activeSection, setActiveSection] = useState<NavSection | null>(
    navComponents[0]?.type ?? null
  )

  const renderCore = (type: NavSection) => {
    const comp = currentComponents.find((c) => c.type === type)
    if (!comp) return null

    switch (type) {
      case 'dimensions':
        return (
          <ProductDimensions
            configuration={comp as ProductDimensionsComponent}
            predefinedValue={values?.dimensions ?? undefined}
          />
        )
      case 'colors':
        return (
          <ProductColors
            configuration={comp as ProductColorsComponent}
            predefinedValue={values?.colors ?? undefined}
          />
        )
      case 'columns':
        return (
          <ProductColumns
            configuration={comp as ProductColumnsComponent}
            predefinedValue={values?.columns ?? undefined}
            options={(comp as ProductColumnsComponent).options}
          />
        )
      case 'individualColumns':
        return (
          <ProductIndividualColumns
            configuration={comp as ProductIndividualColumnsComponent}
            activeTab={activeColumnTab}
            onActiveTabChange={setActiveColumnTab}
            onActiveColumnChange={setSelectedColumnIndex}
          />
        )
      case 'select':
        return (
          <ProductSelect
            configuration={comp as ProductSelectComponent}
            predefinedValue={values?.select ?? undefined}
          />
        )
      case 'furniture': {
        const furnitureConfig = {
          ...(comp as ProductFurnitureComponent),
          is3DEnabled: true,
        }
        return (
          <ProductFurniture
            configuration={{ ...furnitureConfig, furnitureType: 'stand' }}
            predefinedValue={values?.furniture ?? undefined}
          />
        )
      }
      default:
        return null
    }
  }

  const heading = (
    <>
      {/* h2, not h1: the prerender-safe canonical h1 for this route is rendered
          by <SeoContentSection> (main.tsx), outside the lazy route boundary. */}
      <h2 className={styles.visuallyHiddenTitle}>
        <FormattedMessage id="meta.header.configurator.stand" />
      </h2>
      <h2 className={styles.title}>
        <FormattedMessage id={name} />
      </h2>
    </>
  )

  const priceSection = priceComponent ? (
    <div className={styles.priceCont}>
      <ProductPrice
        onAddItem={(price, quote) => {
          const screenshot =
            furnitureViewerRef.current?.captureScreenshot() || undefined
          addItem('stand', currentComponents, {
            ...(values ?? {}),
            price,
            screenshot,
            quote,
          })
        }}
        configuration={priceComponent}
        predefinedValue={values?.price ?? undefined}
        productName={name}
      />
    </div>
  ) : null

  const controls = isMobile && activeSection && navComponents.length > 0 ? (
    <>
      <div className={styles.mobileNavChips}>
        <ButtonSelect<NavSection>
          options={
            navComponents.map((c) => ({
              value: c.type as NavSection,
              label: SECTION_LABELS[c.type as NavSection].id,
            })) as ButtonOptionsType<NavSection>[]
          }
          defaultSelected={activeSection}
          size="medium"
          className={styles.mobileNavChipsRow}
          onChange={(val) => setActiveSection(val as NavSection)}
        />
      </div>

      <div className={styles.mobileCorePanel}>
        {renderCore(activeSection)}
      </div>

      {(() => {
        const colorsComponent = navComponents.find((c) => c.type === 'colors')
        if (colorsComponent && colorsComponent.type !== activeSection) {
          return (
            <div key="colors-hidden" style={{ display: 'none' }}>
              {renderCore('colors')}
            </div>
          )
        }
        return null
      })()}
    </>
  ) : (
    <>
      {currentComponents.map((component, index) => (
        <div key={index + component.type}>{getComponent(component)}</div>
      ))}
    </>
  )

  return (
    <StandQuoteProvider
      sku="stand"
      chipboardId={chipboardId}
      width={furniture3DProps.width}
      height={furniture3DProps.height}
      depth={furniture3DProps.depth}
      plintHeight={furniture3DProps.currentPlintHeight}
      columns={furniture3DProps.columns}
      sections={furniture3DProps.sections}
      openingType={furniture3DProps.openingType}
      columnWidths={furniture3DProps.columnWidths}
      columnConfigurationsWithOptions={
        furniture3DProps.columnConfigurationsWithOptions
      }
      columnConfigurations={
        furniture3DProps.columnConfigurations as
          | ColumnConfigurationType[]
          | undefined
      }
    >
    <ConfiguratorPageShell
      viewer={
        <FurnitureViewer
          ref={furnitureViewerRef}
          {...furniture3DProps}
          selectedColumnIndex={selectedColumnIndex}
          onColumnClick={handleColumnClick}
          onDeselectFunctionReady={handleDeselectFunctionReady}
          onShare={() => setIsShareModalOpen(true)}
          onExport={() => furnitureViewerRef.current?.exportModel()}
        />
      }
      renderThumbnails={renderThumbnail ? [renderThumbnail] : []}
      heading={heading}
      priceSection={priceSection}
      controls={controls}
      renderImages={renderImages}
      showOrderProgress
      showInfoBar
      modals={
        urlConfigCtx?.config ? (
          <ShareConfigModal
            isOpen={isShareModalOpen}
            onClose={() => setIsShareModalOpen(false)}
            config={urlConfigCtx.config}
            product="stand"
            locale={locale}
          />
        ) : undefined
      }
    />
    </StandQuoteProvider>
  )
}
